import React, { useState, useEffect } from 'react';
//import { useUser } from './UserContext';
import { RoomTable, BasicInfo, ProfileTable, TimeBasedAdjustments } from './HeatingSettings_Components.js'
import ThermostatWizard from './ThermostatWizard.js'
//import  sampleConfig from './sample_config.json'
import { t } from './i18n.js'

const HeatingSettings = ({ initialHeatingConfigs, onSave, onCreateThermostat, saveStatus }) => {
  const [heatingConfig, setHeatingConfig] = useState(initialHeatingConfigs);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Sync local state when props change (after data refresh from wizard save)
  useEffect(() => {
    setHeatingConfig(initialHeatingConfigs);
  }, [initialHeatingConfigs]);
  //const [heatingConfig, setHeatingConfig] = useState(sampleConfig);
 
  //console.log("Save status:", saveStatus)
  //console.log ("Initial HeatingCOnfigs",heatingConfig)
  if (!heatingConfig || Object.keys(heatingConfig).length === 0) {
    return <div>{t('common.loading')}</div>;
  }

  // Check if summer mode is active
  const isSummerModeActive = heatingConfig.basic_configs?.summer_mode?.active;

const handleSchemeChange = (newActiveScheme) => {
    setHeatingConfig((prevConfig) => ({
        ...prevConfig,
        basic_configs: {
            ...prevConfig.basic_configs,
            schemes: {  // ✅ Ensure we update schemes.active
                ...prevConfig.basic_configs.schemes,
                active: newActiveScheme
            }
        }
    }));
};

const handleToggleEnabled = (profileId, field, isEnabled) => {
  console.log ("Handling toggle", profileId, field, isEnabled)
  setHeatingConfig((prevConfig) => {
    const updatedProfiles = prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        return {
          ...profile,
          [field]: isEnabled,
        };
      }
      return profile;
    });
    return { ...prevConfig, profile_configs: updatedProfiles };
  });
};

const handlePriceChange = (profileId, rangeId, scheme, value) => {
  const parsedValue = value === '' ? 0 : parseFloat(value);  // Handle empty strings

  setHeatingConfig((prevConfig) => {
    const updatedProfiles = prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        const updatedPriceRanges = profile.price_ranges.map((range) => {
          if (range.id === rangeId) {
            return {
              ...range,
              [scheme]: parsedValue, // Ensure value is parsed as a number
            };
          }
          return range;
        });
        return { ...profile, price_ranges: updatedPriceRanges };
      }
      return profile;
    });

    return { ...prevConfig, profile_configs: updatedProfiles };
  });
};

const handleReserveChange = (profileId, field, value) => {
  const parsedValue = value === '' ? 0 : parseFloat(value);  // Handle empty strings

  setHeatingConfig((prevConfig) => {
    const updatedProfiles = prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        return {
          ...profile,
          reserve_heating: {
            ...profile.reserve_heating,
            [field]: parsedValue, // Ensure value is parsed as a number
          },
        };
      }
      return profile;
    });

    return { ...prevConfig, profile_configs: updatedProfiles };
  });
};

const handlePriceDropChange = (profileId, scheme, newValue) => {
  const parsedValue = newValue === '' ? 0 : parseFloat(newValue);  // Handle empty strings

  setHeatingConfig(prevConfig => ({
    ...prevConfig,
    profile_configs: prevConfig.profile_configs.map(profile => 
      profile.id === profileId
        ? {
            ...profile,
            price_based_drops: {
              ...profile.price_based_drops,
              [scheme]: parsedValue // Ensure value is parsed as a number
            }
          }
        : profile
    )
  }));
};

const handleThermostatSettingChange = (profileId, thermostatId, settingKey, updates) => {
  setHeatingConfig((prevConfig) => ({
    ...prevConfig,
    profile_configs: prevConfig.profile_configs.map((profile) =>
      profile.id === profileId
        ? {
            ...profile,
            thermostat_settings: {
              ...profile.thermostat_settings,
              [thermostatId]: {
                ...profile.thermostat_settings[thermostatId],
                [settingKey]: typeof updates === 'object' && !Array.isArray(updates)
                  ? {
                      ...profile.thermostat_settings[thermostatId][settingKey],
                      ...updates, // Apply the dynamic updates for nested settings
                    }
                  : updates, // Directly assign for flat settings
              },
            },
          }
        : profile
    ),
  }));
};

const handleNestedChange = (profileId, settingCategory, thermostatKey, settingKey, newValue) => {
  const parsedValue = newValue === '' || isNaN(newValue) ? 0 : parseFloat(newValue);

  setHeatingConfig((prevConfig) => ({
    ...prevConfig,
    profile_configs: prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        return {
          ...profile,
          [settingCategory]: {
            ...profile[settingCategory],
            [thermostatKey]: {
              ...profile[settingCategory][thermostatKey],  // Keep other fields of the thermostat
              [settingKey]: parsedValue,  // Update only the specific field (limit, hours, reserve_degrees)
            },
          },
        };
      }
      return profile;
    }),
  }));
};

const handleGroupLevelChange = (profileId, settingCategory, settingKey, newValue) => {
  const parsedValue = newValue === '' || isNaN(newValue) ? 0 : parseFloat(newValue);

  setHeatingConfig((prevConfig) => ({
    ...prevConfig,
    profile_configs: prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        return {
          ...profile,
          [settingCategory]: {
            ...profile[settingCategory],
            [settingKey]: parsedValue,  // Only update the specific key (hours or reserve_degrees)
          },
        };
      }
      return profile;
    }),
  }));
};

const handleSummerModeChange = (updates) => {
  setHeatingConfig((prevConfig) => ({
    ...prevConfig,
    basic_configs: {
      ...prevConfig.basic_configs,
      summer_mode: {
        ...prevConfig.basic_configs.summer_mode,
        ...updates,
      },
    },
  }));
};

const handleUpdateAdjustment = (id, updatedAdjustment) => {
  console.log("Updating Adjustment:", id, updatedAdjustment);

  setHeatingConfig((prevConfigs) => {
    // Create a deep copy of the basic_configs object to ensure React notices the change
    const updatedBasicConfigs = {
      ...prevConfigs.basic_configs,
      time_based_adjustments: {
        ...prevConfigs.basic_configs.time_based_adjustments,
        [id]: updatedAdjustment, // Update the specific adjustment
      },
    };

    return {
      ...prevConfigs,
      basic_configs: updatedBasicConfigs, // Replace basic_configs with the updated copy
    };
  });
};

const handleTimeBasedAdjustmentToggle = (profileId, fieldPath, value) => {
  setHeatingConfig((prevConfig) => {
    const updatedProfiles = prevConfig.profile_configs.map((profile) => {
      if (profile.id === profileId) {
        const fieldParts = fieldPath.split('.');
        const adjustmentId = fieldParts[1]; // Extract adjustment ID (e.g., 'night_reduction', 'day_reduction', 'weekend_reduction')
        const schemeKey = fieldParts[2]; // Extract scheme key (e.g., 'enabled', 'value')

        // Clone the adjustments to avoid mutation
        const updatedTimeBasedAdjustments = { 
          ...profile.time_based_adjustments 
        };

        // Ensure the specific adjustment exists, or initialize if it doesn't
        if (!updatedTimeBasedAdjustments[adjustmentId]) {
          updatedTimeBasedAdjustments[adjustmentId] = {}; // Initialize empty object if missing
        }

        // Update the specific scheme key with the new value
        updatedTimeBasedAdjustments[adjustmentId] = {
          ...updatedTimeBasedAdjustments[adjustmentId],
          [schemeKey]: value,
        };

        return {
          ...profile,
          time_based_adjustments: updatedTimeBasedAdjustments,
        };
      }
      return profile;
    });

    return { ...prevConfig, profile_configs: updatedProfiles };
  });
};

  const handleSave = () => {
    // Send the updated heatingConfig to the parent component
    onSave(heatingConfig);
  };

  const handleCreateThermostat = async (wizardData) => {
    // Pass the wizard data to the parent component for API handling
    if (onCreateThermostat) {
      return await onCreateThermostat(wizardData);
    }
    return { success: false, error: 'No save handler configured' };
  };

  return (
    <div className="container">
      <h2>{t('heating.title')}</h2>

      {/* Summer Mode Notification */}
      {isSummerModeActive && (
        <h4 style={{ fontStyle: 'italic', color: '#666', marginBottom: '20px' }}>
          {t('heating.summerMode')}
        </h4>
      )}
      
      <BasicInfo
        basicInfo={heatingConfig.basic_configs}
        onSchemeChange={handleSchemeChange}
        onSummerModeChange={handleSummerModeChange}
      />
      
      <RoomTable
        roomData={heatingConfig.room_configs}
      />

      {heatingConfig.wizard_metadata && (
        <div style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
          <span
            style={{
              color: '#3b82f6',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.9rem'
            }}
            onClick={() => setIsWizardOpen(true)}
          >
            + {t('wizard.addThermostat')}
          </span>
        </div>
      )}

      <TimeBasedAdjustments
        basicInfo={heatingConfig.basic_configs}
        onUpdateAdjustment={handleUpdateAdjustment}
      />
      
      <ProfileTable 
        profileConfigs={heatingConfig.profile_configs}
        basicInfo={heatingConfig.basic_configs}
        onTimeBasedAdjustmentToggle={handleTimeBasedAdjustmentToggle}
        onToggleEnabled={handleToggleEnabled}
        onToggleOptimisationEnabled={handleToggleEnabled}
        onPriceChange={handlePriceChange}
        onReserveChange={handleReserveChange}
        onReserveHeatingAfternoonChange={handleGroupLevelChange}
        onPriceDropChange={handlePriceDropChange}
        onThermostatSettingChange={handleThermostatSettingChange}
      />
      <p></p>
      {saveStatus === 'success' && <p><i>{t('common.saveSuccess')}</i></p>}
      {saveStatus === 'error' && <p>{t('common.saveError')}</p>}
      <button onClick={handleSave}>{t('common.save')}</button>

      <ThermostatWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSave={handleCreateThermostat}
        wizardMetadata={heatingConfig?.wizard_metadata}
        basicConfigs={heatingConfig?.basic_configs}
      />
    </div>
  );
};

export default HeatingSettings;